import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Bell, Mail, Save, Plus, X, Calendar as CalendarIcon, Clock, BarChart3, ListOrdered, TrendingUp, Cog as CogIcon, AlertCircle } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS_META = [
  { key: "kpis", label: "KPIs Globais (OEE, Disp., Perf., Qual.)", icon: BarChart3 },
  { key: "ranking", label: "Ranking Pior OEE (5 maquinas)", icon: ListOrdered, weeklyOnly: true },
  { key: "trend", label: "Tendencia Diaria", icon: TrendingUp, weeklyOnly: true },
  { key: "machine_detail", label: "Detalhe por Maquina e Ordem", icon: CogIcon },
  { key: "stop_reasons", label: "Top Causas de Paragem", icon: AlertCircle },
];

function ScheduleEditor({ title, schedule, onChange, weeklyOnly = false, testid }) {
  const [emailInput, setEmailInput] = useState("");
  const addEmail = () => {
    const e = emailInput.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Email invalido"); return; }
    if (schedule.recipients.includes(e)) { toast.error("Email ja existe"); return; }
    onChange({ ...schedule, recipients: [...schedule.recipients, e] });
    setEmailInput("");
  };
  const removeEmail = (e) => onChange({ ...schedule, recipients: schedule.recipients.filter(r => r !== e) });
  const toggleSection = (key) => onChange({ ...schedule, sections: { ...schedule.sections, [key]: !schedule.sections[key] } });

  return (
    <Card data-testid={testid}>
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            {weeklyOnly ? <CalendarIcon className="h-5 w-5 text-amber-600" /> : <Mail className="h-5 w-5 text-indigo-600" />}
            {title}
          </CardTitle>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={schedule.enabled}
              onChange={(e) => onChange({ ...schedule, enabled: e.target.checked })}
              className="w-4 h-4"
              data-testid={`${testid}-enabled`}
            />
            <span className={schedule.enabled ? "text-green-700 font-medium" : "text-gray-400"}>
              {schedule.enabled ? "Activo" : "Desactivado"}
            </span>
          </label>
        </div>
      </CardHeader>
      <CardContent className={`pt-4 space-y-5 ${!schedule.enabled ? "opacity-50 pointer-events-none" : ""}`}>
        {/* Hora de envio */}
        <div>
          <label className="text-xs font-bold text-gray-600 flex items-center gap-1.5 mb-2">
            <Clock className="h-3.5 w-3.5" /> Hora de Envio (UTC)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="23"
              value={schedule.hour_utc}
              onChange={(e) => onChange({ ...schedule, hour_utc: parseInt(e.target.value) || 0 })}
              className="w-20 border rounded-lg px-3 py-1.5 text-sm font-mono text-center"
              data-testid={`${testid}-hour`}
            />
            <span className="text-sm text-gray-600">h00 UTC = {String((schedule.hour_utc + 1) % 24).padStart(2, "0")}h00 PT (inverno)</span>
          </div>
        </div>

        {/* Destinatarios */}
        <div>
          <label className="text-xs font-bold text-gray-600 flex items-center gap-1.5 mb-2">
            <Mail className="h-3.5 w-3.5" /> Destinatarios ({schedule.recipients.length})
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="email@dominio.com"
              className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
              data-testid={`${testid}-email-input`}
            />
            <Button type="button" size="sm" onClick={addEmail} data-testid={`${testid}-email-add`}>
              <Plus className="h-3.5 w-3.5 mr-1" />Adicionar
            </Button>
          </div>
          {schedule.recipients.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">⚠ Nenhum destinatario - email nao sera enviado</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {schedule.recipients.map((e, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs text-blue-900">
                  <Mail className="h-3 w-3" />
                  {e}
                  <button onClick={() => removeEmail(e)} className="text-red-500 hover:text-red-700 font-bold" data-testid={`${testid}-email-remove-${i}`}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Seccoes */}
        <div>
          <label className="text-xs font-bold text-gray-600 mb-2 block">Seccoes do Email</label>
          <div className="space-y-1.5">
            {SECTIONS_META.map(s => {
              if (s.weeklyOnly && !weeklyOnly) return null;
              const Icon = s.icon;
              const enabled = !!schedule.sections[s.key];
              return (
                <label key={s.key} className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-all ${enabled ? "bg-blue-50 border-blue-300" : "bg-white border-gray-200"}`} data-testid={`${testid}-section-${s.key}`}>
                  <input type="checkbox" checked={enabled} onChange={() => toggleSection(s.key)} className="w-4 h-4" />
                  <Icon className={`h-4 w-4 ${enabled ? "text-blue-600" : "text-gray-400"}`} />
                  <span className={`text-sm ${enabled ? "text-gray-900" : "text-gray-500"}`}>{s.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function NotificationSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/notification-settings`)
      .then(res => {
        // Defensivo: garantir que sections tem todas as chaves esperadas
        const defaultSections = { kpis: true, ranking: false, trend: false, machine_detail: true, stop_reasons: true };
        const data = res.data;
        if (data?.daily) data.daily.sections = { ...defaultSections, ...(data.daily.sections || {}) };
        if (data?.weekly) data.weekly.sections = { ...defaultSections, ranking: true, trend: true, ...(data.weekly.sections || {}) };
        setSettings(data);
      })
      .catch(() => toast.error("Erro a carregar definicoes"));
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, {
        daily: settings.daily,
        weekly: settings.weekly,
      });
      toast.success("Definicoes guardadas");
    } catch (e) {
      showError(e, "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="text-center py-12 text-gray-400">A carregar...</div>;
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-8 w-8 text-indigo-600" />
            Configuracao de Notificacoes
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Personalize os emails automaticos do Dashboard OEE: destinatarios, hora, secoes</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="notif-save-btn" className="bg-indigo-600 hover:bg-indigo-700">
          <Save className="h-4 w-4 mr-2" />{saving ? "A guardar..." : "Guardar Alteracoes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ScheduleEditor
          title="Email Diario OEE"
          schedule={settings.daily}
          onChange={(d) => setSettings({ ...settings, daily: d })}
          testid="schedule-daily"
        />
        <ScheduleEditor
          title="Email Semanal OEE (Segunda-feira)"
          schedule={settings.weekly}
          onChange={(w) => setSettings({ ...settings, weekly: w })}
          weeklyOnly
          testid="schedule-weekly"
        />
      </div>

      <Card className="bg-slate-50 border-dashed">
        <CardContent className="p-4 text-xs text-gray-600 space-y-1">
          <div className="font-semibold text-gray-800 mb-1">Notas:</div>
          <div>• <strong>Hora UTC</strong>: 7h UTC = 8h PT inverno / 9h PT verao (utilize o convertor mental conforme estacao).</div>
          <div>• <strong>Desactivar scheduler</strong>: nao impede envio manual via botoes "Email Diario" / "Email Semanal" no Dashboard OEE.</div>
          <div>• <strong>Destinatarios</strong>: a lista e' usada como "To". Para enviar a mais pessoas adicione todos os emails aqui.</div>
          <div>• Apenas <strong>administradores</strong> podem editar estas definicoes.</div>
        </CardContent>
      </Card>
    </div>
  );
}
